<script setup lang="ts">
import { computed, ref, watch } from 'vue';
import { Tabs, TabsContent } from '@/components/ui/tabs';
import InspectorTabsMenu from '@/components/DataTable/InspectorTabsMenu.vue';
import SearchResultInspector from '@/components/DataTable/SearchResultInspector.vue';
import PlaylistUsageTracksInspectorContent from '@/components/PlaylistUsageTracksInspectorContent.vue';
import PlaylistSwitchingContactBreakdown from '@/components/PlaylistSwitchingContactBreakdown.vue';
import EditableScheduleList from '@/components/schedules/EditableScheduleList.vue';
import { buildEntityColumns } from '@/composables/entityColumns';
import { inspectorExcludeFieldsFor } from '@/composables/searchInspectorRegistry';
import { useModelRecordQuery } from '@/composables/api/useModelRecordQuery';
import { takePlaylistContainerRequest, watchPlaylistContainerSection } from '@/composables/playlistContainerSectionRequest';
import type { TableColumnStaticProps } from '@/types/tableTypes';

/**
 * The one inspector for a playlist container — a Custom Playlist or a Bucket.
 *
 * There used to be two. Opening a custom list from Search showed Details and
 * Tracks; opening the SAME list from the Playlist Switching report showed
 * Contacts and Tracks and no Details at all. Same record, different screens,
 * and nothing stopping them drifting further apart. This is both of them.
 *
 * Sections:
 *   Details   the generic field sections
 *   Schedules every zone's schedules for this list, grouped by zone (custom
 *             lists only — a bucket is never scheduled)
 *   Switches  who switched to this list, and when (custom lists only — a
 *             bucket is not something a contact can switch to)
 *   Tracks    the resolved tracks as track cards
 *
 * It can be opened either way: Search hands over a full record row, while the
 * switching report has only a list id and the report's date range.
 */
const props = withDefaults(
    defineProps<{
        /** The full record, when the caller has one (Search does). */
        row?: Record<string, unknown> | null;
        /** The list id, when the caller has only that (the switching report does). */
        locationZoneCustomPlaylistId?: number | null;
        /** Every column of the entity (visible or not) in table order. */
        columns?: TableColumnStaticProps[];
        /** Column ids the header already shows. */
        excludeFields?: string[];
        entityId?: string;
        /** Which section to land on. Ignored when this record has no such section. */
        initialSection?: string | null;
        /** Report window for the Switches section. */
        from?: string;
        to?: string;
        listLabel?: string;
    }>(),
    {
        row: null,
        locationZoneCustomPlaylistId: null,
        columns: () => [],
        excludeFields: () => [],
        entityId: '',
        initialSection: null,
        from: undefined,
        to: undefined,
        listLabel: undefined,
    }
);

const isBucket = computed(() => props.entityId === 'Bucket');
const sourceType = computed<'bucket' | 'custom_playlist'>(() =>
    isBucket.value ? 'bucket' : 'custom_playlist'
);

const recordId = computed<number | null>(() => {
    const id = Number(props.row?.id ?? props.locationZoneCustomPlaylistId);
    return Number.isFinite(id) ? id : null;
});

/**
 * Details needs a record AND the entity's columns. Search hands over both; the
 * switching report has only a list id, and Details rendered blank for it — the
 * section was offered and showed nothing, which is worse than not offering it.
 *
 * So when the caller gives no row, fetch one. The query key is shared with the
 * overlay header, so the title and the fields come from one request. It stays
 * disabled while a row IS supplied, so the Search path costs nothing.
 */
const needsOwnRecord = computed(() => props.row === null);

const ownRecordQuery = useModelRecordQuery(
    () => props.entityId,
    () => (needsOwnRecord.value ? recordId.value : null)
);

const detailsRow = computed<Record<string, unknown>>(
    () => props.row ?? ownRecordQuery.data.value ?? {}
);

/** Likewise the columns: an id-only open has none, so build them for the entity. */
const detailsColumns = computed<TableColumnStaticProps[]>(() =>
    props.columns.length > 0 ? props.columns : buildEntityColumns(props.entityId)
);

const detailsExcludeFields = computed<string[]>(() =>
    props.excludeFields.length > 0 ? props.excludeFields : inspectorExcludeFieldsFor(props.entityId)
);

function nestedDisplayString(value: unknown): string | null {
    if (value && typeof value === 'object' && 'display_string' in value) {
        const name = (value as { display_string?: unknown }).display_string;
        return typeof name === 'string' && name.trim() !== '' ? name : null;
    }
    return null;
}

/** The list's name for the schedule cards' first line. */
const listName = computed<string | null>(() => {
    const name = detailsRow.value.display_string ?? detailsRow.value.name;
    return typeof name === 'string' && name.trim() !== '' ? name : null;
});

/**
 * One list of sections, built in one place, so the switcher and the panels can
 * never disagree about what this record has.
 */
const sections = computed(() => [
    { value: 'details', label: 'Details' },
    ...(isBucket.value ? [] : [{ value: 'schedules', label: 'Schedules' }]),
    // "Switches" rather than the old "Contacts": the section is about the act of
    // switching to this list, and it counts switches, not people.
    ...(isBucket.value ? [] : [{ value: 'switches', label: 'Switches' }]),
    { value: 'tracks', label: 'Tracks' },
]);

const DEFAULT_SECTION = 'details';
const SECTION_STORAGE_KEY = 'playlist-container-inspector:tab:v1';

function isOffered(value: string | null | undefined): boolean {
    return sections.value.some((section) => section.value === value);
}

function readPersistedSection(): string {
    const stored = localStorage.getItem(SECTION_STORAGE_KEY);
    return isOffered(stored) ? (stored as string) : DEFAULT_SECTION;
}

// An explicit request wins over what was last looked at, but only when this
// record actually offers it — a bucket opened with initialSection "switches"
// would otherwise land on a section that is not in its list. A click on a
// card's count leaves a request just before the card opens this inspector.
const initialRequest = takePlaylistContainerRequest(props.entityId, recordId.value);
const requestedSection = initialRequest?.section ?? props.initialSection;
const activeSection = ref<string>(isOffered(requestedSection) ? (requestedSection as string) : readPersistedSection());

/** The schedule to point at in the Schedules section: the one clicked on the schedule search. */
const highlightScheduleId = ref<string | null>(initialRequest?.highlightId ?? null);

// A count can also be clicked while this inspector is already open. It may be the card's or the
// heading's. A schedule clicked on the schedule search asks the same way, naming itself.
watchPlaylistContainerSection(
    () => props.entityId,
    () => recordId.value,
    (section, highlightId) => {
        highlightScheduleId.value = highlightId;
        onSectionChange(section);
    },
);

watch(activeSection, (section) => localStorage.setItem(SECTION_STORAGE_KEY, section));

// Reopening for a different record can drop the section out from under us.
watch(sections, () => {
    if (!isOffered(activeSection.value)) {
        activeSection.value = DEFAULT_SECTION;
    }
});

function onSectionChange(value: string | number) {
    if (isOffered(String(value))) {
        activeSection.value = String(value);
    }
}
</script>

<template>
    <Tabs
        :model-value="activeSection"
        class="flex min-h-0 flex-1 flex-col gap-4"
        data-testid="playlist-container-inspector"
        @update:model-value="onSectionChange"
    >
        <InspectorTabsMenu
            :model-value="activeSection"
            :items="sections"
            @update:model-value="onSectionChange"
        />

        <TabsContent value="details">
            <p
                v-if="needsOwnRecord && ownRecordQuery.isLoading.value"
                class="text-muted-foreground text-sm"
            >
                Loading…
            </p>
            <p
                v-else-if="needsOwnRecord && ownRecordQuery.isError.value"
                class="text-muted-foreground text-sm"
                data-testid="playlist-container-details-error"
            >
                Couldn't load {{ entityId }} #{{ recordId }}.
            </p>
            <SearchResultInspector
                v-else
                :row="detailsRow"
                :columns="detailsColumns"
                :exclude-fields="detailsExcludeFields"
                :entity-id="entityId"
            />
        </TabsContent>

        <TabsContent v-if="!isBucket" value="schedules" data-testid="playlist-schedules">
            <EditableScheduleList
                :items="detailsRow.location_zone_custom_playlist_schedules"
                :list-id="recordId"
                :owner-zone-id="detailsRow.location_zone_id"
                :owner-zone-name="nestedDisplayString(detailsRow.location_zone)"
                :list-name="listName"
                :highlight-schedule-id="highlightScheduleId"
                density="card"
            />
        </TabsContent>

        <TabsContent v-if="!isBucket" value="switches">
            <PlaylistSwitchingContactBreakdown
                v-if="recordId !== null"
                :location-zone-custom-playlist-id="recordId"
                :from="from"
                :to="to"
                :list-label="listLabel"
            />
        </TabsContent>

        <TabsContent value="tracks" data-testid="playlist-tracks">
            <PlaylistUsageTracksInspectorContent
                v-if="recordId !== null"
                class="!p-0"
                :source-type="sourceType"
                :source-id="recordId"
                hide-list-header
            />
        </TabsContent>
    </Tabs>
</template>
